import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { IntegrationKeyGuide } from '../components/shared/IntegrationKeyGuide';
import {
  MessageCircle, Send, Settings as SettingsIcon, Copy, ExternalLink,
  Loader2, Check, AlertTriangle, Phone
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const Empty = ({ icon: Icon, title, hint }) => (
  <div className="text-center py-16 text-gray-500">
    <Icon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
    <h3 className="font-medium text-gray-700">{title}</h3>
    {hint && <p className="text-xs mt-1 max-w-md mx-auto">{hint}</p>}
  </div>
);

export default function WhatsAppPage() {
  const [tab, setTab] = useState('inbox');
  // ─── Config tab ───────────────────────────────
  const [cfg, setCfg] = useState({ phone_number_id: '', system_access_token: '', verify_token: '', business_display_name: '', enabled: true });
  const [cfgMeta, setCfgMeta] = useState({ webhook_url: '', default_verify_token: '', graph_api_version: '' });
  const [tokenPreview, setTokenPreview] = useState('');
  const [saving, setSaving] = useState(false);

  // ─── Inbox tab ────────────────────────────────
  const [threads, setThreads] = useState([]);
  const [activeWa, setActiveWa] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const inboxTimer = useRef(null);

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/whatsapp/config`);
      const c = r.data?.config || {};
      setCfgMeta({
        webhook_url: r.data?.webhook_url || '',
        default_verify_token: r.data?.default_verify_token || '',
        graph_api_version: r.data?.graph_api_version || '',
      });
      setCfg((p) => ({
        ...p,
        phone_number_id: c.phone_number_id || '',
        verify_token: c.verify_token || '',
        business_display_name: c.business_display_name || '',
        enabled: c.enabled !== false,
      }));
      setTokenPreview(c.system_access_token_preview || '');
    } catch { /* ignore */ }
  }, []);

  const loadThreads = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/whatsapp/conversations`);
      setThreads(r.data?.threads || []);
    } catch { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async (waId) => {
    if (!waId) return;
    try {
      const r = await axios.get(`${API}/api/whatsapp/messages/${waId}`);
      setMessages(r.data?.messages || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadConfig();
    loadThreads();
    inboxTimer.current = setInterval(loadThreads, 15000);
    return () => clearInterval(inboxTimer.current);
  }, [loadConfig, loadThreads]);

  useEffect(() => {
    if (activeWa) loadMessages(activeWa);
  }, [activeWa, loadMessages]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        phone_number_id: cfg.phone_number_id,
        verify_token: cfg.verify_token || cfgMeta.default_verify_token,
        business_display_name: cfg.business_display_name,
        enabled: cfg.enabled,
      };
      // only send token if user typed one
      if (cfg.system_access_token && cfg.system_access_token.trim()) {
        payload.system_access_token = cfg.system_access_token.trim();
      }
      await axios.put(`${API}/api/whatsapp/config`, payload);
      toast.success('WhatsApp config saved');
      setCfg((p) => ({ ...p, system_access_token: '' }));
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save config');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!activeWa || !draft.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/whatsapp/send`, { to: activeWa, text: draft.trim() });
      setMessages((m) => [...m, r.data.message]);
      setDraft('');
      loadThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };
  const isConfigured = !!tokenPreview && !!cfg.phone_number_id;

  return (
    <div className="page-shell-wide" data-testid="whatsapp-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <MessageCircle className="h-7 w-7 text-emerald-500" /> WhatsApp Business<PageInfoTooltip pageKey="whatsapp-business" />
          </h1>
          <p className="text-gray-500 mt-1">Two-way customer conversations from inside the helpdesk.</p>
        </div>
        {isConfigured ? (
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50 gap-1.5 py-1.5 px-3">
            <Check className="h-3.5 w-3.5" /> Connected
          </Badge>
        ) : (
          <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 gap-1.5 py-1.5 px-3">
            <AlertTriangle className="h-3.5 w-3.5" /> Not connected
          </Badge>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="inbox" data-testid="whatsapp-tab-inbox"><MessageCircle className="h-4 w-4 mr-2" />Inbox</TabsTrigger>
          <TabsTrigger value="config" data-testid="whatsapp-tab-config"><SettingsIcon className="h-4 w-4 mr-2" />Setup</TabsTrigger>
        </TabsList>

        {/* ─── INBOX ───────────────────────────────── */}
        <TabsContent value="inbox" className="mt-6">
          {!isConfigured ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Phone className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-700">Connect WhatsApp first</h3>
                <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto mb-4">
                  Add your Meta Business Phone Number ID and System Access Token in the Setup tab to start receiving messages here.
                </p>
                <Button onClick={() => setTab('config')} className="bg-emerald-600 hover:bg-emerald-700">
                  Go to setup
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {/* Thread list */}
              <Card className="md:col-span-4">
                <CardHeader className="pb-3"><CardTitle className="text-sm">Threads</CardTitle></CardHeader>
                <CardContent className="p-0 max-h-[60vh] overflow-y-auto">
                  {threads.length === 0
                    ? <Empty icon={MessageCircle} title="No conversations yet" hint="They'll appear here when a customer messages your WhatsApp number." />
                    : threads.map((t) => (
                      <button
                        key={t.wa_contact_id}
                        onClick={() => setActiveWa(t.wa_contact_id)}
                        className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 ${activeWa === t.wa_contact_id ? 'bg-emerald-50' : ''}`}
                        data-testid={`thread-${t.wa_contact_id}`}
                      >
                        <div className="font-medium text-sm text-gray-900">+{t.wa_contact_id}</div>
                        <div className="text-xs text-gray-500 truncate mt-0.5">{t.last_message?.text || '(media)'}</div>
                        <div className="text-[10px] text-gray-400 mt-1">{t.count} messages · {new Date(t.last_message?.created_at).toLocaleDateString()}</div>
                      </button>
                    ))}
                </CardContent>
              </Card>

              {/* Conversation pane */}
              <Card className="md:col-span-8 flex flex-col">
                <CardHeader className="pb-3"><CardTitle className="text-sm">{activeWa ? `+${activeWa}` : 'Select a thread'}</CardTitle></CardHeader>
                <CardContent className="flex-1 flex flex-col p-0">
                  <div className="flex-1 max-h-[50vh] overflow-y-auto px-4 py-2 space-y-2">
                    {!activeWa
                      ? <Empty icon={MessageCircle} title="No thread selected" />
                      : messages.length === 0
                        ? <Empty icon={MessageCircle} title="No messages yet" />
                        : messages.map((m, i) => (
                          <div key={i} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                              m.direction === 'outbound' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-900'
                            }`}>
                              <div className="whitespace-pre-wrap">{m.text}</div>
                              <div className={`text-[10px] mt-1 ${m.direction === 'outbound' ? 'text-emerald-100' : 'text-gray-500'}`}>
                                {new Date(m.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))}
                  </div>
                  {activeWa && (
                    <div className="border-t p-3 flex gap-2">
                      <Textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={2}
                        placeholder="Reply…"
                        className="flex-1 text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }}
                        data-testid="whatsapp-reply-input"
                      />
                      <Button onClick={handleSend} disabled={sending || !draft.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-send-btn">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ─── CONFIG ──────────────────────────────── */}
        <TabsContent value="config" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-base">Step 1 · Meta Business credentials</CardTitle>
                <IntegrationKeyGuide provider="whatsapp_cloud" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4 max-w-2xl">
              <div>
                <Label htmlFor="pnid">Phone Number ID</Label>
                <Input
                  id="pnid"
                  value={cfg.phone_number_id}
                  onChange={(e) => setCfg((p) => ({ ...p, phone_number_id: e.target.value.trim() }))}
                  placeholder="106xxxxxxxxxxxx"
                  className="mt-2 font-mono"
                  data-testid="whatsapp-phone-number-id"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Meta Business Manager → WhatsApp → API Setup → "From" phone number → ID below it.
                </p>
              </div>
              <div>
                <Label htmlFor="tok">System Access Token</Label>
                <Input
                  id="tok"
                  type="password"
                  value={cfg.system_access_token}
                  onChange={(e) => setCfg((p) => ({ ...p, system_access_token: e.target.value }))}
                  placeholder={tokenPreview || 'EAAxxxxxxxxxxxxxxxxxxx…'}
                  className="mt-2 font-mono"
                  data-testid="whatsapp-token"
                />
                {tokenPreview && (
                  <p className="text-xs text-emerald-600 mt-1">Currently saved: {tokenPreview} (leave blank to keep)</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Use a permanent System User token, not a 24-hour test token. Business Settings → System Users → Generate new token.
                </p>
              </div>
              <div>
                <Label htmlFor="biz">Business display name</Label>
                <Input
                  id="biz"
                  value={cfg.business_display_name}
                  onChange={(e) => setCfg((p) => ({ ...p, business_display_name: e.target.value }))}
                  placeholder="Acme Coffee"
                  className="mt-2"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((p) => ({ ...p, enabled: v }))} />
                <span className="text-sm">Enabled</span>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-save-config">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save credentials
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Step 2 · Webhook configuration (Meta side)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-w-2xl text-sm">
              <p className="text-gray-600">
                In Meta Business Manager → WhatsApp → Configuration → Webhooks, paste the values below.
                Then subscribe to the <code>messages</code> field.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="bg-gray-50 rounded border p-2.5">
                  <div className="text-gray-500 mb-1">Callback URL</div>
                  <div className="font-mono break-all flex items-center justify-between gap-2">
                    <span>https://konnect.kaharian.com{cfgMeta.webhook_url}</span>
                    <button onClick={() => copy(`https://konnect.kaharian.com${cfgMeta.webhook_url}`)} className="text-emerald-700 hover:text-emerald-900">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 rounded border p-2.5">
                  <div className="text-gray-500 mb-1">Verify Token</div>
                  <div className="font-mono break-all flex items-center justify-between gap-2">
                    <span>{cfg.verify_token || cfgMeta.default_verify_token}</span>
                    <button onClick={() => copy(cfg.verify_token || cfgMeta.default_verify_token)} className="text-emerald-700 hover:text-emerald-900">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 rounded border p-2.5">
                  <div className="text-gray-500 mb-1">Subscribe to</div>
                  <div className="font-mono">messages</div>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Need a custom verify token? Save one in Step 1 — it will replace the default.
              </div>
              <div>
                <a
                  className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 text-xs"
                  href="https://business.facebook.com/wa/manage" target="_blank" rel="noreferrer"
                >
                  Open Meta Business Manager <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
